import { parseISO, isValid } from 'date-fns';

/**
 * Safely parse a date value that could be a string or Date object
 * @param dateValue - The date value to parse
 * @returns Parsed Date object or null if invalid
 */
export function parseDate(dateValue: string | Date | null | undefined): Date | null {
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    return isValid(dateValue) ? dateValue : null;
  }

  if (typeof dateValue === 'string') {
    // Try to parse ISO format first (more reliable)
    const isoDate = parseISO(dateValue);
    if (isValid(isoDate)) return isoDate;

    // Fallback to Date constructor
    const constructorDate = new Date(dateValue);
    return isValid(constructorDate) ? constructorDate : null;
  }

  return null;
}

/**
 * Check if a date is today or in the future
 * @param date - Date to check
 * @returns true if date is today or future, false otherwise
 */
export function isDateTodayOrFuture(date: Date | null): boolean {
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);

  return checkDate >= today;
}

/**
 * Check if start date is before end date
 * @param startDate - Start date
 * @param endDate - End date
 * @returns true if start is before end, false otherwise
 */
export function isValidDateRange(startDate: Date | null, endDate: Date | null): boolean {
  if (!startDate || !endDate) return false;
  return startDate < endDate;
}

/**
 * Validate a complete date range for campaign/order dates
 * @param startDate - Campaign start date
 * @param endDate - Campaign end date
 * @returns Object with validation result and error message
 */
export function validateDateRange(
  startDate: string | Date | null,
  endDate: string | Date | null
): {
  isValid: boolean;
  error?: string;
} {
  const parsedStart = parseDate(startDate);
  const parsedEnd = parseDate(endDate);

  if (!parsedStart) {
    return { isValid: false, error: 'Invalid start date' };
  }

  if (!parsedEnd) {
    return { isValid: false, error: 'Invalid end date' };
  }

  if (!isDateTodayOrFuture(parsedStart)) {
    return { isValid: false, error: 'Start date must be today or in the future' };
  }

  if (!isValidDateRange(parsedStart, parsedEnd)) {
    return { isValid: false, error: 'End date must be after start date' };
  }

  return { isValid: true };
}
